import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause } from 'lucide-react';
// Fixed waveform heights — looks like a real audio waveform
const WAVEFORM = [0.3, 0.5, 0.4, 0.7, 0.6, 0.9, 0.5, 0.8, 0.4, 0.6, 0.3, 0.7, 0.5, 0.9, 0.4, 0.6, 0.8, 0.5, 0.7, 0.4, 0.6, 0.3, 0.8, 0.5, 0.7, 0.4, 0.9, 0.6, 0.5, 0.7, 0.4, 0.6, 0.8, 0.3, 0.7, 0.5, 0.6, 0.4, 0.8, 0.5, 0.7, 0.3, 0.6, 0.9, 0.4, 0.7, 0.5, 0.6, 0.4, 0.8, 0.5, 0.7, 0.3, 0.6, 0.4, 0.9];
export function AudioPlayer() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Attempt autoplay on mount
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    // Wait for metadata then attempt autoplay
    const attemptAutoplay = () => {
      audio.play().catch(() => {
        // Browser blocked autoplay — user must interact first, that's fine
      });
    };
    if (audio.readyState >= 1) {
      attemptAutoplay();
    } else {
      audio.addEventListener('loadedmetadata', attemptAutoplay, {
        once: true
      });
    }
  }, []);
  // Use YouTube embed URL as audio source via a proxy approach
  // Since direct YouTube audio isn't possible, we use the video embed
  // with a visible (but tiny) iframe that the user can interact with
  // For actual deployment, replace AUDIO_URL with a direct MP3 link
  const AUDIO_URL = `https://www.youtube.com/embed/cY1k4Bx0k_4?autoplay=0&controls=0&enablejsapi=1`;
  const formatTime = (s: number) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
  };
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = x / rect.width;
    audio.currentTime = ratio * duration;
  };
  return <div className="w-full max-w-2xl mx-auto mb-8" data-id="element-1">
      {/* Hidden video element used for audio playback */}
      <video ref={audioRef} src="https://cdn.magicpatterns.com/uploads/nUcMnj2hGNqo2TVCnFEJFq/How To Play The Game.mp4" className="hidden" onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onEnded={() => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    }} onTimeUpdate={() => {
      const audio = audioRef.current;
      if (!audio) return;
      setCurrentTime(audio.currentTime);
      if (audio.duration) setProgress(audio.currentTime / audio.duration * 100);
    }} onLoadedMetadata={() => {
      const audio = audioRef.current;
      if (audio) setDuration(audio.duration);
    }} preload="metadata" data-id="element-2" />

      {/* Terminal Player */}
      <div className="font-mono" style={{
      background: '#050a0f',
      border: '1px solid rgba(201,168,76,0.5)',
      boxShadow: '0 0 20px rgba(201,168,76,0.08), inset 0 0 40px rgba(0,0,0,0.5)'
    }} data-id="element-3">
        {/* Top bar — filename + status */}
        <div className="flex items-center justify-between px-4 py-2" style={{
        borderBottom: '1px solid rgba(201,168,76,0.2)'
      }} data-id="element-4">
          <span className="text-[#c9a84c] text-xs tracking-widest" data-id="element-5">
            {'>'} PREVIEW: HOW_TO_PLAY_THE_GAME.wav
          </span>
          <span className="text-[#c9a84c] text-xs tracking-widest flex items-center gap-2" data-id="element-6">
            <span className="inline-block w-2.5 h-2.5" style={{
            background: isPlaying ? '#c9a84c' : 'transparent',
            border: '1px solid #c9a84c'
          }} data-id="element-7" />
            {isPlaying ? 'PLAYING' : 'PAUSED'}
          </span>
        </div>

        {/* Main row — play button + waveform + time */}
        <div className="flex items-center gap-4 px-4 py-3" data-id="element-8">
          {/* Play / Pause button */}
          <button onClick={togglePlay} className="flex-shrink-0 flex items-center justify-center transition-all duration-150 active:scale-95" style={{
          width: '44px',
          height: '44px',
          border: '1px solid #c9a84c',
          background: 'transparent',
          color: '#c9a84c'
        }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.1)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} data-id="element-9">
            {isPlaying ? <Pause className="w-4 h-4 fill-current" data-id="element-10" /> : <Play className="w-4 h-4 fill-current ml-0.5" data-id="element-11" />}
          </button>

          {/* Waveform + progress */}
          <div className="flex-1 flex flex-col gap-1.5" data-id="element-12">
            {/* Waveform bars */}
            <div className="flex items-end gap-px cursor-pointer relative" style={{
            height: '32px'
          }} onClick={handleSeek} data-id="element-13">
              {WAVEFORM.map((h, i) => {
              const barProgress = i / WAVEFORM.length * 100;
              const isPast = barProgress <= progress;
              return <div key={i} className="flex-1 rounded-sm transition-colors duration-100" style={{
                height: `${h * 100}%`,
                background: isPast ? '#c9a84c' : 'rgba(201,168,76,0.25)',
                boxShadow: isPast && isPlaying ? '0 0 3px rgba(201,168,76,0.6)' : 'none'
              }} data-id="element-14" />;
            })}
            </div>

            {/* Progress track */}
            <div className="w-full cursor-pointer" style={{
            height: '2px',
            background: 'rgba(201,168,76,0.15)'
          }} onClick={handleSeek} data-id="element-15">
              <div style={{
              height: '100%',
              width: `${progress}%`,
              background: '#c9a84c',
              transition: 'width 0.3s linear'
            }} data-id="element-16" />
            </div>
          </div>

          {/* Time */}
          <div className="flex-shrink-0 text-xs tracking-widest text-right" style={{
          color: '#c9a84c',
          minWidth: '80px'
        }} data-id="element-17">
            {formatTime(currentTime)} /{' '}
            {duration > 0 ? formatTime(duration) : '--:--'}
          </div>
        </div>
      </div>

      {/* Note below player */}
      <p className="text-center text-[10px] font-mono mt-2" style={{
      color: 'rgba(201,168,76,0.3)'
    }} data-id="element-18">
        AUDIOBOOK PREVIEW • CHAPTER 1
      </p>
    </div>;
}